import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { ESTADO_COMPONENTE } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { Inbox, Search, ChevronRight, ArrowUpRight, Tag } from "lucide-react";

// Transiciones (espejo backend/workflow.py - simplificado)
const TRANSICIONES = {
  pendiente_diseno:       ["en_diseno", "cancelado"],
  en_diseno:              ["aaff_pendiente_cliente", "aaff_recibido", "cancelado"],
  aaff_pendiente_cliente: ["aaff_recibido", "cancelado"],
  aaff_recibido:          ["en_produccion", "cancelado"],
  en_produccion:          ["producido", "cancelado"],
  producido:              ["en_logistica"],
  pendiente_proveedor:    ["recibido_almacen", "recibido_pdv", "cancelado"],
  recibido_almacen:       ["en_logistica"],
  recibido_pdv:           ["en_logistica"],
  cliente_aporta:         ["en_logistica"],
  en_logistica:           ["entregado"],
  entregado:              [],
  cancelado:              [],
};

function abrirEtiqueta(componenteId) {
  const token = localStorage.getItem("dnf_access_token");
  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/componentes/${componenteId}/etiqueta.pdf`, {
    headers: { Authorization: `Bearer ${token}` },
  })
    .then((r) => r.blob())
    .then((blob) => window.open(URL.createObjectURL(blob), "_blank"));
}

export default function MisPendientes() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [estadoFiltro, setEstadoFiltro] = useState("todos");
  const [q, setQ] = useState("");

  const load = async () => {
    const { data } = await api.get("/dashboard/mis-pendientes");
    setItems(data.items || []);
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, []);

  const cambiarEstado = async (cid, nuevo) => {
    await api.patch(`/componentes/${cid}`, { estado: nuevo });
    load();
  };

  const filtered = items.filter((c) => {
    if (estadoFiltro !== "todos" && c.estado !== estadoFiltro) return false;
    if (q) {
      const t = `${c.nombre} ${c.peticion_codigo || ""} ${c.peticion_nombre || ""} ${c.cliente_nombre || ""} ${c.marca || ""}`.toLowerCase();
      if (!t.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  // Agrupar por petición
  const grouped = useMemo(() => {
    const g = {};
    filtered.forEach((c) => {
      const k = c.peticion_id;
      if (!g[k]) g[k] = { peticion_id: k, codigo: c.peticion_codigo, nombre: c.peticion_nombre, cliente: c.cliente_nombre, marca: c.marca, items: [] };
      g[k].items.push(c);
    });
    return Object.values(g);
  }, [filtered]);

  const estadosDisponibles = Array.from(new Set(items.map((c) => c.estado))).sort();

  const rolLabel = (user?.roles || []).filter((r) => r !== "admin").map((r) => r[0].toUpperCase() + r.slice(1)).join(", ") || "Admin";

  return (
    <div className="p-8 max-w-[1800px]">
      <PageHeader
        title="Mis pendientes"
        subtitle={`Lo que está esperándote (${rolLabel}). Cambia el estado de cada componente cuando lo termines.`}
      />

      <div className="bg-zinc-900/40 border border-zinc-800 rounded-xl p-3 mb-4 flex flex-wrap gap-2 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-2.5 w-3.5 h-3.5 text-zinc-500" />
          <Input value={q} onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar componente, petición, cliente o marca…"
            data-testid="mispend-search"
            className="pl-8 bg-zinc-950 border-zinc-800 h-9 text-sm" />
        </div>
        <Select value={estadoFiltro} onValueChange={setEstadoFiltro}>
          <SelectTrigger data-testid="mispend-filter-estado" className="w-[220px] bg-zinc-900 border-zinc-800 h-9 text-xs"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
            <SelectItem value="todos">Todos los estados ({items.length})</SelectItem>
            {estadosDisponibles.map((e) => {
              const lbl = ESTADO_COMPONENTE[e] || { label: e, icon: "•" };
              const count = items.filter((it) => it.estado === e).length;
              return <SelectItem key={e} value={e}>{lbl.icon} {lbl.label} ({count})</SelectItem>;
            })}
          </SelectContent>
        </Select>
        <div className="text-xs text-zinc-500 ml-auto">{filtered.length} / {items.length} pendientes</div>
      </div>

      {grouped.length === 0 && (
        <div className="bg-emerald-500/5 border border-emerald-500/30 rounded-xl p-10 text-center">
          <Inbox className="w-10 h-10 text-emerald-400 mx-auto mb-3" />
          <div className="text-lg font-semibold text-emerald-300">¡Todo al día!</div>
          <div className="text-sm text-zinc-400 mt-1">No tienes nada pendiente en este momento.</div>
        </div>
      )}

      <div className="space-y-4" data-testid="mispend-list">
        {grouped.map((g) => (
          <div key={g.peticion_id} className="bg-zinc-900/40 border border-zinc-800 rounded-xl overflow-hidden">
            <Link to={`/peticiones/${g.peticion_id}`}
              className="flex items-center justify-between gap-3 px-4 py-3 bg-zinc-900/60 border-b border-zinc-800 hover:bg-zinc-900/80 transition-colors">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-zinc-100 truncate">{g.nombre}</div>
                <div className="text-[11px] text-zinc-500">
                  {g.codigo} · {g.cliente || "—"} {g.marca && `· 🏷 ${g.marca}`}
                </div>
              </div>
              <div className="flex items-center gap-2 text-xs text-amber-300">
                {g.items.length} pendiente{g.items.length !== 1 ? "s" : ""}
                <ArrowUpRight className="w-3.5 h-3.5" />
              </div>
            </Link>
            <div>
              {g.items.map((c) => {
                const est = ESTADO_COMPONENTE[c.estado] || ESTADO_COMPONENTE.pendiente;
                const next = TRANSICIONES[c.estado] || [];
                return (
                  <div key={c.id} data-testid={`mispend-row-${c.id}`}
                    className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-zinc-800/60 last:border-b-0 hover:bg-zinc-900/40 transition-colors">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-zinc-200 truncate flex items-center gap-2">
                        {c.imagen_url && <img src={c.imagen_url} alt="" className="w-8 h-8 object-cover rounded border border-zinc-700" />}
                        <span>{c.nombre}</span>
                        {c.cantidad > 1 && <span className="text-zinc-500 text-xs">×{c.cantidad}</span>}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${est.color}`}>{est.icon} {est.label}</span>
                        {c.ancho_cm && c.alto_cm && <span className="text-[10px] text-zinc-500">{c.ancho_cm}×{c.alto_cm}cm</span>}
                        {c.bulto_numero && <span className="text-[10px] text-emerald-400">📦 Bulto {c.bulto_numero}</span>}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      {c.bulto_numero && (
                        <Button size="sm" variant="ghost"
                          onClick={() => abrirEtiqueta(c.id)}
                          data-testid={`mispend-etiqueta-${c.id}`}
                          className="h-7 w-7 p-0 text-emerald-400 hover:text-emerald-300" title="Imprimir etiqueta">
                          <Tag className="w-3.5 h-3.5" />
                        </Button>
                      )}
                      {next.length > 0 && (
                        <Select value="" onValueChange={(v) => v && cambiarEstado(c.id, v)}>
                          <SelectTrigger data-testid={`mispend-next-${c.id}`}
                            className="h-7 w-auto px-2 bg-amber-500/15 border-amber-500/40 hover:bg-amber-500/25 text-amber-200 text-xs gap-1">
                            <span>Avanzar</span><ChevronRight className="w-3 h-3" />
                          </SelectTrigger>
                          <SelectContent className="bg-zinc-950 border-zinc-800 text-zinc-100">
                            {next.map((k) => {
                              const e = ESTADO_COMPONENTE[k] || { label: k, icon: "→" };
                              return <SelectItem key={k} value={k}>{e.icon} {e.label}</SelectItem>;
                            })}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
